import { useCallback } from 'react';
import { Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { SMSDataSharedConstants, SMSDataV3 } from '@frontend/api-sms-data';
import { getUser, hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppFlagStore } from '@frontend/shared';
import { sentry } from '@frontend/tracking';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { ChatTypes, ContextMenuActions, useAlert } from '@frontend/design-system';
import { MediaInteractionUtils } from '../utils';

type SMSContextActions = 'tag' | 'copy' | 'copy-image' | 'download-image' | 'retry' | 'delete';

type UseSMSContextActionsArgs = {
  sms: SMS;
  hiddenActions?: SMSContextActions[];
  openTagSelection?: () => void;
  onResend?: () => void;
};

export const useSMSContextActions = ({ sms, hiddenActions, openTagSelection, onResend }: UseSMSContextActionsArgs) => {
  const { t } = useTranslation('thread-body');
  const alert = useAlert();
  const user = getUser();
  const { featureFlags } = useAppFlagStore();

  const aclEnforcementEnabled = !!featureFlags[sms.locationId]?.get('nwx:messaging-inbox-fe-acl-enforcement')?.value;
  const hasDeleteACL = aclEnforcementEnabled ? hasSchemaACL(sms.locationId, Permission.SMS_INBOX_MESSAGE_DELETE) : true;
  const isSending = Object.entries(SMSDataSharedConstants.SENDING_STATUS_DATA).every(
    ([key, value]) => sms[key as keyof typeof SMSDataSharedConstants.SENDING_STATUS_DATA] === value
  );
  const isDeleted = !!sms.deletedAt;

  const { mutateAsync: deleteSms } = SMSDataV3.Mutations.useDeleteSMSMutation({
    options: {
      onSuccess: () => {
        alert.success(t('Message deleted'));
      },
      onError: () => {
        alert.error(t('Error deleting message'));
      },
    },
  });

  const handleDelete = async () =>
    deleteSms({
      smsId: sms.id,
      locationId: sms.locationId,
      userId: user?.userID ?? '',
      createdAt: sms.createdAt,
      _otherOptions: {
        threadId: sms.threadId,
        isAutomated: !!sms.autogeneratedBy,
      },
    });

  const canCopy = !isDeleted;
  const handleCopySMSBody = () => {
    if (sms.body) {
      navigator.clipboard.writeText(sms.body);
      alert.success(t('Message Copied'));
    } else {
      alert.error(t('No text to copy'));
    }
  };
  const handleCopyImage = (mediaId: string) => {
    try {
      MediaInteractionUtils.copyImage(mediaId, sms.locationId);
      alert.success(t('Image Copied'));
    } catch (err) {
      alert.error(t('Error copying image'));
      sentry.error({
        error: err,
        topic: 'messages',
        severityLevel: 'error',
        addContext: {
          name: 'Message object & media item',
          context: {
            sms,
            mediaId,
            errMessage: 'Error copying image from message',
          },
        },
      });
    }
  };
  const handleDownloadImage = (mediaId: string) => {
    const mediaItem = sms.media.find((item) => item.mediaId === mediaId);
    try {
      MediaInteractionUtils.downloadImage({
        mediaId,
        filename: mediaItem?.filename ?? `${sms.personPhone}-${t('image-download')}`,
        locationId: sms.locationId,
      });
      alert.success(t('Image Downloaded'));
    } catch (err) {
      alert.error(t('Error downloading image'));
      sentry.error({
        error: err,
        topic: 'messages',
        severityLevel: 'error',
        addContext: {
          name: 'Message object & media item',
          context: {
            sms,
            mediaId,
            errMessage: 'Error downloading image from message',
          },
        },
      });
    }
  };

  const { isLoading: isResending, mutateAsync: resendMessage } = SMSDataV3.Mutations.useResendSMSMutation({
    options: {
      onSuccess: () => {
        onResend?.();
      },
      onError: () => {
        alert.error(t('Error resending message'));
      },
    },
    optimisticUpdate: true,
  });

  const retryableStatuses = [Status.STATUS_ERROR, Status.STATUS_NOT_SENT];
  const canRetry = !isResending && retryableStatuses.includes(sms.status) && !hiddenActions?.includes('retry');

  const handleRetry = async () =>
    resendMessage({
      smsId: sms.id,
      threadId: sms.threadId,
      locationId: sms.locationId,
      isAutomated: !!sms.autogeneratedBy,
    });

  const getActions = useCallback(
    (mediaId?: string): { primaryActions: ChatTypes.PrimaryAction[]; moreActions: ContextMenuActions } => {
      const result: { primaryActions: ChatTypes.PrimaryAction[]; moreActions: ContextMenuActions } = {
        primaryActions: [],
        moreActions: [],
      };

      if (openTagSelection && !hiddenActions?.includes('tag')) {
        result.primaryActions.push({
          label: t('Tag Message'),
          icon: 'label',
          onClick: openTagSelection,
          trackingId: `${InboxPrefixes.Tag}-tag-message`,
        });
      }

      if (canCopy && !mediaId && !hiddenActions?.includes('copy')) {
        result.moreActions.push({
          label: t('Copy Message'),
          Icon: () => <Icon name='copy' size={18} />,
          onClick: handleCopySMSBody,
          trackingId: `${InboxPrefixes.Thread}-message-action-copy`,
        });
      }

      if (mediaId) {
        if (!hiddenActions?.includes('copy-image')) {
          result.moreActions.push({
            label: t('Copy Image'),
            Icon: () => <Icon name='copy' size={18} />,
            onClick: () => handleCopyImage(mediaId),
            trackingId: `${InboxPrefixes.Thread}-message-action-copy-image`,
          });
        }
        if (!hiddenActions?.includes('download-image')) {
          result.moreActions.push({
            label: t('Download Image'),
            Icon: () => <Icon name='download' size={18} />,
            onClick: () => handleDownloadImage(mediaId),
            trackingId: `${InboxPrefixes.Thread}-message-action-download-image`,
          });
        }
      }

      if (canRetry && !isDeleted && !hiddenActions?.includes('retry')) {
        result.moreActions.push({
          label: t('Retry Send'),
          Icon: () => <Icon name='update' size={18} />,
          onClick: handleRetry,
          trackingId: `${InboxPrefixes.Thread}-message-action-retry`,
        });
      }

      if (!isSending && !isDeleted && !hiddenActions?.includes('delete')) {
        result.moreActions.push({
          label: t('Delete from Thread'),
          Icon: () => <Icon name='trash' size={18} />,
          onClick: handleDelete,
          disabled: !hasDeleteACL,
          hoverLabel: !hasDeleteACL ? t('You do not have permission to delete messages.') : undefined,
        });
      }

      return result;
    },
    [
      openTagSelection,
      canCopy,
      handleCopySMSBody,
      handleCopyImage,
      handleDownloadImage,
      t,
      canRetry,
      isSending,
      hasDeleteACL,
      isDeleted,
      JSON.stringify(hiddenActions),
    ]
  );

  return {
    getActions,
  };
};
